// web/js/player-columns/PlayerColumn.tsx
//
// Per-column React tree for a single player column. Owns its own file fetch,
// renders breadcrumb, title, versions dropdown, slot, player-nav (when single),
// actions bar, and info tabs.
//
// The component uses the shared `fetchFileData` helper from player-loader so
// response-shape changes propagate automatically. The legacy comment/transcript
// code reads `comment_file_id` / `window.fileID`, which are kept in sync by
// `subscribePlayerUtilsToPlayerColumns()` in `html/js/player_utils.js`.

import React, { useEffect, useRef, useState } from 'react';
import { fetchFileData } from '../player-loader/loadFile';
import type { GetFileResponse, WorkflowSummary } from '../player-loader/loadFile';
import { PlayerSlot } from './PlayerSlot';
import { resolveViewerType } from './resolveViewerType';
import VersionsDropdown from '../player-versions/VersionsDropdown';
import PlayerActionsBar from '../player-actions/PlayerActionsBar';
import PlayerInfoTabs from '../player-info-tabs/PlayerInfoTabs';
import type { PlayerColumnProps } from './types';
import type { EncodingStatus } from "../player/types";
import KollabEncodingProgress from "../encoding-progress/KollabEncodingProgress";
import { hideTooltipsIn } from '../hooks/useTooltip';
import PathBreadcrumb from '../path-breadcrumb/PathBreadcrumb';

// Web-component JSX declaration: `<progress-bar-circular>` is a custom
// element registered via customElements.define() elsewhere. Declared here
// so TypeScript accepts it as a JSX tag in the render below.
declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'progress-bar-circular': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          width?: string | number;
          height?: string | number;
          'image-url'?: string;
          progress?: string | number;
        },
        HTMLElement
      >;
    }
  }
}

interface ProjectContext {
  projectId: number;
  isAdmin: boolean;
  canDownload: boolean;
  canShare: boolean;
  canModifyOthersFiles: boolean;
}

function readProjectContext(): ProjectContext {
  const w = window as any;
  const project = w.jsconnect?.project ?? {};
  return {
    projectId: project.id ?? -1,
    isAdmin: !!project.is_admin,
    canDownload: !!project.can_download,
    canShare: !!project.can_share,
    canModifyOthersFiles: !!project.can_modify_others_files,
  };
}

export function PlayerColumn({
  colId,
  fileId,
  linkId,
  isFolderLink,
  initialData,
  isActive,
  isOnly,
  onSelect,
  onFileChange,
  onFileDataLoaded,
  onViewerReady,
  onAddClick,
  onCloseClick,
  onAddVersionClick,
  autoplay,
  onWorkflowChange,
  tabConfig,
  syncedSession,
  refetchToken,
  onMediaReady,
}: PlayerColumnProps) {
  const [fileData, setFileData] = useState<GetFileResponse | null>(
    initialData ?? null,
  );
  // Wrapper ref so we can scope tipsy tooltip cleanup to elements inside
  // this column on unmount. tipsy popups are parented to <body>, not the
  // trigger element, so React unmounting the trigger leaves the popup
  // orphaned in the DOM.
  //
  // We can't use a plain JSX ref directly in the unmount cleanup: React
  // clears JSX refs to null BEFORE useEffect cleanup runs, so the ref would
  // be empty by the time we tried to query inside it. Instead we keep a
  // mirror ref (`lastWrapperRef`) that we manually update from a separate
  // effect — that ref is plain user state, never cleared by React, so the
  // unmount cleanup can read the last-known wrapper element from it.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const lastWrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    lastWrapperRef.current = wrapperRef.current;
  });
  // Keep the `onFileDataLoaded` callback in a ref so we can fire it from
  // effects without adding it to dependency arrays (that would cause the
  // fetch effect to rerun on every rerender from the manager).
  const onFileDataLoadedRef = useRef(onFileDataLoaded);
  useEffect(() => { onFileDataLoadedRef.current = onFileDataLoaded; }, [onFileDataLoaded]);
  // Fire once on first mount if we rendered from pre-injected initialData.
  // Subsequent loads fire from inside the fetch effect below.
  const firedInitialDataRef = useRef(false);
  useEffect(() => {
    if (firedInitialDataRef.current) return;
    if (initialData) {
      firedInitialDataRef.current = true;
      onFileDataLoadedRef.current?.(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [error, setError] = useState<string | null>(null);
  // Track the fileId the initialData was for, so we only skip the fetch on the
  // very first mount. A later fileId change (e.g. version switch) MUST fetch.
  const initialFileIdRef = useRef<number | null>(
    initialData ? fileId : null,
  );
  // Project-level workflow list. Only the initial PHP-injected data carries
  // this — the Rust /file/{id} endpoint omits it. Workflows are project-level
  // and don't change when the file changes, so cache the last seen value and
  // reuse it across fetch-driven file switches within this column.
  const workflowsRef = useRef<WorkflowSummary[]>(
    initialData?.workflows ?? [],
  );
  if (fileData?.workflows && fileData.workflows !== workflowsRef.current) {
    workflowsRef.current = fileData.workflows;
  }

  useEffect(() => () => hideTooltipsIn(lastWrapperRef.current), []);

  useEffect(() => {
    // Skip the initial fetch if we already have pre-injected data for this fileId.
    if (initialFileIdRef.current !== null && fileId === initialFileIdRef.current) {
      // Clear so any subsequent change to this same fileId (unlikely) still refetches.
      initialFileIdRef.current = null;
      return;
    }
    let cancelled = false;
    setError(null);
    setFileData(null);
    fetchFileData(fileId, linkId ?? undefined)
      .then(data => {
        if (!cancelled) {
          setFileData(data);
          onFileDataLoadedRef.current?.(data);
        }
      })
      .catch(err => {
        if (!cancelled) setError(err?.message ?? String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [fileId, linkId, refetchToken]);
  
  const wrapperClass = `player-column @container ${isActive && !isOnly ? ' active' : ''}`;
  const handleClick = () => onSelect(colId);
  const handleAddClick = () => onAddClick?.();
  const handleCloseClick = () => onCloseClick?.(colId);
  const handleAddVersionClick = (fileId: number) => onAddVersionClick?.(fileId);
  const handleWorkflowChange = (workflowId: number) => onWorkflowChange?.(workflowId);

  if (error) {
    return (
      <div ref={wrapperRef} className={wrapperClass} onClick={handleClick}>
        <div className="player-column-error">Failed to load: {error}</div>
      </div>
    );
  }

  if (!fileData) {
    return (
      <div ref={wrapperRef} className={wrapperClass} onClick={handleClick}>
        <div className="player-column-loading">
          <div className="spinner centered"/>
        </div>
      </div>
    );
  }

  const ctx = readProjectContext();
  const effectiveLinkId = linkId ?? null;
  const isLink = effectiveLinkId != null;

  const currentVersion = fileData.versions.find(v => v.id === fileData.id);

  // Props for VersionsDropdown — drives the versions selector shown below the player.
  const versionsProps = {
    fileId: fileData.id,
    currentVersionTitle: currentVersion?.version_title || fileData.title,
    versions: fileData.versions.map(v => ({
      id: v.id,
      version: 0,
      title: v.version_title,
      isCurrent: v.id === fileData.id,
      thumbUrl: v.thumbnail_url,
    })),
    linkId: effectiveLinkId,
    onVersionChange: (newId: number) => onFileChange(colId, newId),
    shouldShowVersionComparison: isOnly,
    onVersionComparisonClick: (fileId: number) => onAddVersionClick?.(fileId),
  };

  // Props for PlayerActionsBar — download/share/subscribe/favorite buttons and workflow menu.
  const actionsProps = {
    fileId: fileData.id,
    colId: colId,
    fileTypeId: fileData.file_type_id,
    linkId: effectiveLinkId,
    departmentId: fileData.department_id,
    parentPath: fileData.path,
    projectId: fileData.project_id,
    aliasTo: fileData.alias_to,
    canDownload: ctx.canDownload && fileData.original_file_url != null,
    proxies: fileData.proxies.map((p) => ({
      proxyId: p.id,
      width: p.width,
      height: p.height,
      fileExtension: (p.file_extension || '').toUpperCase(),
      size: p.size,
    })),
    sourceFile: {
      fileExtension: (fileData.file_extension || '').toUpperCase(),
      width: fileData.metadata.width,
      height: fileData.metadata.height,
      size: fileData.size,
    },
    canShare: !isLink && ctx.canShare,
    isSubscribed: fileData.user_info.is_subscribed,
    isFavorited: fileData.user_info.is_favorite,
    relatedFiles: [],
    currentWorkflowId: fileData.workflow_id,
    workflows:
      fileData.workflow_name && fileData.workflow_id > 0
        ? [{ id: fileData.workflow_id, name: fileData.workflow_name }]
        : [],
    isOwnerOrAdmin: fileData.user_info.is_file_owner || ctx.isAdmin || ctx.canModifyOthersFiles,
    hasProxy: fileData.proxies.length > 0,
    canEncode: fileData.can_encode,
    isBeingConverted: fileData.is_encoding,
    hideActionsButton: !isOnly,
  };

  const headerClasses = `player-title flex items-center gap-2 text-3xl mb-2 mt-4 @2xl:text-5xl @2xl:mb-5 @2xl:mt-6${!isOnly ? " limited-height" : ""}`;

  // Click-to-activate scope: only the player area (or "equivalent component")
  // should activate the column on click — not the title, breadcrumb, or info
  // tabs. PlayerSlot binds onActivate on the .player-stage div for video/
  // audio/image and on a wrapper div for document/text/commentstream.
  // The download viewer is the exception: there's no other selectable element
  // for it, so the entire column wrapper takes the click instead.
  const viewerType = resolveViewerType(fileData);
  const isDownloadOnly = viewerType === 'download' || fileData.download_only;
  const wrapperOnClick = isDownloadOnly ? handleClick : undefined;
  const slotOnActivate = isDownloadOnly ? undefined : handleClick;

  return (
    <div ref={wrapperRef} className={wrapperClass} onClick={wrapperOnClick}>
      <div className="player-column-top-bar">
      {(!isLink || isFolderLink) && <PathBreadcrumb data={fileData} linkId={effectiveLinkId} projectId={ctx.projectId} />}
      <div className="spacer"></div>
      {isOnly && !isLink && !syncedSession?.enabled &&
          <div className="player-column-corner-button hidden md:block" role="presentation">
            <button className="tooltip compare-btn"
            role="button"
            tabIndex={0}
            onClick={handleAddClick}
            title="Compare with another file"
          >
            <div className="fa-icon fa-icon-plus-solid"/> Compare
          </button>
      </div>
      }
      {!isOnly &&
          <div className="player-column-corner-button" role="presentation">
            <button className="player-column-corner-button tooltip"
                 role="button"
                 tabIndex={0}
                 onClick={handleCloseClick}
                 title="Close this file"
            >
              <div className="fa-icon fa-icon-circle-x-outline"/>
            </button>
          </div>
      }
      </div>

      <PlayerSlot fileData={fileData} colId={colId} isActive={isActive} onActivate={slotOnActivate} autoplay={autoplay} onViewerReady={onViewerReady} syncedSession={syncedSession} onMediaReady={onMediaReady} />

      {/*
        PlayerNav (prev/next file navigation) is mounted globally via
        #player-nav-root in player.php — not inside this column tree.
        When more than one column is open it is hidden by the
        subscribePlayerUtilsToPlayerColumns subscriber in player_utils.js,
        which toggles #player-nav-root's display based on column count.
      */}

      <h1
          className={headerClasses}
          style={{wordBreak: 'break-all'}}
          lang={fileData.metadata.language || 'en'}
      >
        {fileData.is_encoding && <KollabEncodingProgress fileId={fileData.id} doneClickCallback={() => {
          window.playerColumns.reloadColumn(colId);
          document.getElementById('encoding_alert')?.remove();
        }} />}
        {fileData.title}
      </h1>

      {fileData.versions.length > 1 && <VersionsDropdown {...versionsProps} />}

      {/* Add dummy button to make sure side-by-side players take up the same vertical space */}
      {fileData.versions.length < 2 && !isOnly && <button className="version-selector-button" style={{opacity: 0}} disabled={true} aria-hidden={true}>Dummy</button>}

      <PlayerActionsBar {...actionsProps} />

      {!effectiveLinkId && (
        <PlayerInfoTabs
          colId={colId}
          fileId={fileData.id}
          fileTypeId={fileData.file_type_id}
          projectId={ctx.projectId}
          isProjectAdmin={ctx.isAdmin}
          canModifyFile={fileData.user_info.is_file_owner || ctx.isAdmin || ctx.canModifyOthersFiles}
          workflows={fileData.workflows ?? workflowsRef.current}
          onWorkflowChange={(workflowId: number) => handleWorkflowChange(workflowId)}
          tabConfig={tabConfig}
        />
      )}
    </div>
  );
}

export default PlayerColumn;
